// User presence ("last connection") tracking.
//
// Mirrors what the iOS/Android apps do for the `last_connection` field on the
// tenant-scoped user doc: mark the user "Active now" while the app is in the
// foreground, and stamp a leave time when it goes to the background. On web we
// drive this from page-visibility + unload events and route the write through
// the backend `POST /v1/presence` endpoint (server-authoritative timestamps),
// rather than writing Firestore directly.
//
// Contract: { state: 'active' | 'away' }. The server resolves identity + tenant
// from the session token and writes last_connection = 0 (active) or Unix
// seconds (away).
import axios from 'axios';
import config from '@/config/constants.js';

const PRESENCE_URL = config.api_url + 'presence';

let active = false;        // is presence tracking currently wired?
let lastState = null;      // 'active' | 'away' — transition guard (avoid redundant writes)
let onVisibility = null;
let onLeave = null;

function authToken() {
  try {
    return localStorage.getItem('auth_token');
  } catch (e) {
    return null;
  }
}

// "active" is a routine call — the default axios Authorization header (kept in
// sync with the current session token in auth_success) carries auth.
function sendActive() {
  axios.post(PRESENCE_URL, { state: 'active' }).catch(() => {});
}

// "away" fires on tab-hide / unload and must survive the page being torn down:
// keepalive lets the request complete after navigation, and we attach the token
// explicitly because there's no guarantee the axios default is applied here.
function sendAway() {
  const token = authToken();
  if (!token) return;
  try {
    fetch(PRESENCE_URL, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ state: 'away' }),
    }).catch(() => {});
  } catch (e) {
    /* best-effort — nothing to do if the browser refuses the keepalive call */
  }
}

function setState(state) {
  if (!active) return;
  if (lastState === state) return;   // only write on real transitions
  lastState = state;
  if (state === 'active') sendActive();
  else sendAway();
}

// Mark the current session active now (used on login / tenant-switch into a
// tenant). Safe to call when not started — it no-ops until startPresence runs.
export function markActive() {
  setState('active');
}

// Mark the current session away. Used before logout / tenant-switch out, so the
// user doesn't stay frozen as "Active now" in the tenant they're leaving. Uses
// whatever token is currently stored — call this BEFORE swapping the token.
export function markAway() {
  setState('away');
}

// Wire visibility/unload listeners (idempotent) and mark active immediately.
export function startPresence() {
  if (active) return;
  active = true;
  lastState = null;

  onVisibility = () => {
    if (document.visibilityState === 'visible') markActive();
    else markAway();
  };
  onLeave = () => markAway();

  document.addEventListener('visibilitychange', onVisibility);
  // pagehide is the reliable "page is going away" signal (incl. bfcache);
  // beforeunload is a belt-and-suspenders fallback.
  window.addEventListener('pagehide', onLeave);
  window.addEventListener('beforeunload', onLeave);

  // Initial state: visible tabs are active; if the app booted hidden, stamp away.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    markAway();
  } else {
    markActive();
  }
}

// Remove listeners and reset. Call on logout (after a final markAway).
export function stopPresence() {
  if (!active) return;
  active = false;
  lastState = null;
  if (onVisibility) document.removeEventListener('visibilitychange', onVisibility);
  if (onLeave) {
    window.removeEventListener('pagehide', onLeave);
    window.removeEventListener('beforeunload', onLeave);
  }
  onVisibility = null;
  onLeave = null;
}

export function isPresenceActive() {
  return active;
}
